import { EDatasourceType, ESdChangeReason, IDatasourceDto, IDatasourceRuntimePin } from "@kuika/kuika-cl-model";
import {
  ICreateLibraryRequest,
  IUpdateLibraryRequest,
  PackageType
} from "@kuika/kuika-cl-model/src/models/package-management";
import Editor from "@monaco-editor/react";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import { HttpStatusCode } from "axios";
import { debounce } from "lodash";
import * as monacoEditor from "monaco-editor";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { CompletionItemKind, LanguageServerService } from "../../../services/language-server-service";
import { PackageManagementService } from "../../../services/package-management-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { pythonEditorOptions } from "../../../shared/utility/monaco-editor-config";
import { injectMonacoStyleFixes } from "../../../shared/utility/monaco-style-fixes";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { AICodeGenerator, EAICodeGeneratorType } from "../../common/ai/ai-code-generator";
import { IPlannedCodeInsertion } from "../../common/ai/ai-code-insertion";
import { AiPendingEdit } from "../../common/ai/ai-pending-edit";
import { AiPendingEditBar } from "../../common/ai/ai-pending-edit-bar";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import ExternalLibrariesButton from "../../common/external-libraries-button/external-libraries-button";
import { TestRunner, ITestRunnerMethod } from "../../common/test-runner/test-runner";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { CodeEditorControls, ICodeEditorControlsState } from "../code-editor-shell/code-editor-controls";
import { CodeEditorHeader } from "../code-editor-shell/code-editor-header";
import {
  codeEditorRenameTestIds,
  getOpeningDatasourceName,
  validateRenameField
} from "../code-editor-shell/code-editor-rename";
import { CodeEditorRenameTitle } from "../code-editor-shell/code-editor-rename-title";
import { CodeEditorSidePanel, ECodeEditorSideTab } from "../code-editor-shell/code-editor-side-panel";
import { CodeEditorSplitter } from "../code-editor-shell/code-editor-splitter";
import { CODE_EDITOR_THEME, defineCodeEditorTheme, watchCodeEditorTheme } from "../code-editor-shell/code-editor-theme";
import { CodeEditorToolbar } from "../code-editor-shell/code-editor-toolbar";
import { requestDatasourceReveal } from "./datasource-reveal";
import { CodeDatasourceSwitcherChip } from "./editor-switcher/code-datasource-switcher-chip";

const DEFAULT_CODE = `def new_function(parameter1: str) -> str:
    # Your code here
    return parameter1
`;

// Selectable Python runtime versions are fetched from the backend runtime catalog (the single source of truth) via
// DatasourceService.GetRuntimeVersions("python") — no hard-coded copy here. The user pins a *logical* version;
// codegen/deploy resolves it to the exact per-RID archive in the content-addressed store.

const mapCompletionKind = (
  monaco: typeof monacoEditor,
  kind: CompletionItemKind
): monacoEditor.languages.CompletionItemKind => {
  switch (kind) {
    case CompletionItemKind.Function:
      return monaco.languages.CompletionItemKind.Function;
    case CompletionItemKind.Method:
      return monaco.languages.CompletionItemKind.Method;
    case CompletionItemKind.Class:
      return monaco.languages.CompletionItemKind.Class;
    case CompletionItemKind.Module:
      return monaco.languages.CompletionItemKind.Module;
    case CompletionItemKind.Property:
      return monaco.languages.CompletionItemKind.Property;
    case CompletionItemKind.Variable:
      return monaco.languages.CompletionItemKind.Variable;
    case CompletionItemKind.Keyword:
      return monaco.languages.CompletionItemKind.Keyword;
    default:
      return monaco.languages.CompletionItemKind.Text;
  }
};

interface IPypiPackageItem {
  id: string;
  version: string;
  description: string;
}

interface IInstalledLibrary {
  id: string;
  name: string;
  version: string;
  description?: string;
}

interface IPypiPagination {
  skip: number;
  take: number;
  total: number;
}

interface IPackageVersion {
  version: string;
}

interface ICreatePythonActionState {
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Undo, wrapping, the font stepper, the caret and the markers — owned by `CodeEditorControls`. */
  controls: ICodeEditorControlsState;
  /** Which side tab is showing; Kuika AI is the only one this editor offers. */
  sideTab: ECodeEditorSideTab;
  /** Folded from the vertical grip, and dragged by it. */
  isSidePanelOpened: boolean;
  sidePanelWidth: number;
  /** The AI edit waiting for the user's confirmation; the editor still holds their own code. */
  pendingAiEdit: IPlannedCodeInsertion | null;
  pypiPackages: IPypiPackageItem[];
  isLoadingPypiPackages: boolean;
  pypiSearchQuery: string;
  pypiPagination: IPypiPagination;
  installedLibraries: IInstalledLibrary[];
  isLoadingInstalledLibraries: boolean;
  activePackageLoading?: string;
  expandedLibrary?: string;
  libraryVersions: Record<string, IPackageVersion[]>;
  isLoadingVersions: boolean;
  runtimeVersions: IDatasourceRuntimePin[];
  /**
   * The committed name. The field mounts only while renaming, and the form hands back mounted
   * fields alone — so this, never the form, is what the hash, the save and the dialogs read.
   */
  datasourceName: string;
  isNameOnEdit: boolean;
}

interface ICreatePythonActionProps {
  editingDatasource?: IDatasourceDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (showModal: boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

// Stable reference for the "no versions" case so the popover's version-loading effect isn't retriggered every render.
const EMPTY_VERSIONS: IPackageVersion[] = [];

class CreatePythonAction extends PureSdComponent<ICreatePythonActionProps & IWithSDProps, ICreatePythonActionState> {
  formRef = React.createRef<FormInstance>();

  editorRef: monacoEditor.editor.IStandaloneCodeEditor | null = null;

  aiPendingEdit: AiPendingEdit = new AiPendingEdit({
    onChange: (pendingAiEdit: IPlannedCodeInsertion | null) => this.setState({ pendingAiEdit })
  });

  controls: CodeEditorControls = new CodeEditorControls({
    onChange: (controls: ICodeEditorControlsState) => this.setState({ controls })
  });

  codeThemeObserver: MutationObserver | undefined;

  diagnosticsAbortController: AbortController | null = null;

  completionProvider: monacoEditor.IDisposable | null = null;

  hoverProvider: monacoEditor.IDisposable | null = null;

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreatePythonActionProps & IWithSDProps) {
    super(props);
    const datasourceName: string = getOpeningDatasourceName(
      props.editingDatasource,
      props.sd.activeCreatingDatasourceName
    );
    this.state = {
      saveStatus: "idle",
      controls: this.controls.state,
      sideTab: "coach",
      isSidePanelOpened: true,
      sidePanelWidth: 288,
      pendingAiEdit: null,
      pypiPackages: [],
      isLoadingPypiPackages: false,
      pypiSearchQuery: "",
      pypiPagination: { skip: 0, take: 20, total: 0 },
      installedLibraries: [],
      isLoadingInstalledLibraries: false,
      activePackageLoading: undefined,
      expandedLibrary: undefined,
      libraryVersions: {},
      isLoadingVersions: false,
      runtimeVersions: [],
      datasourceName,
      // The create picker asks for the name first, so a new file opens named; only a file
      // with no name at all opens with the field.
      isNameOnEdit: !datasourceName
    };
  }

  componentDidMount() {
    this.loadRuntimeVersions();
    this.loadDatasource();
  }

  // Fetch the selectable runtime versions from the catalog-backed endpoint (single source of truth). On a fresh
  // (create) form with no version yet picked, default to the newest once they arrive.
  loadRuntimeVersions = async () => {
    try {
      const response = await DatasourceService.GetRuntimeVersions("python");
      const runtimeVersions = response.data || [];
      this.setState({ runtimeVersions }, () => {
        const current = this.formRef.current?.getFieldValue("RuntimeVersion");
        if (!current && runtimeVersions.length > 0) {
          this.formRef.current?.setFieldsValue({ RuntimeVersion: runtimeVersions[0].versionKey });
          this.objectHash = this.getObjectHash();
        }
      });
    } catch {
      // Leave the list empty; the picker shows no options and save proceeds without a pin (degraded, non-fatal).
    }
  };

  defaultVersionKey = (): string | undefined => this.state.runtimeVersions[0]?.versionKey;

  componentDidUpdate(prevProps: ICreatePythonActionProps & IWithSDProps) {
    if (this.props.editingDatasource?.id !== prevProps.editingDatasource?.id) {
      this.loadDatasource();
    }
    if (
      !this.props.editingDatasource &&
      this.props.sd.activeCreatingDatasourceName &&
      prevProps.sd.activeCreatingDatasourceName === undefined
    ) {
      this.seedName(this.props.sd.activeCreatingDatasourceName);
    }
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  loadDatasource = () => {
    const { editingDatasource } = this.props;
    const name: string = getOpeningDatasourceName(editingDatasource, this.props.sd.activeCreatingDatasourceName);
    const code = editingDatasource?.code || DEFAULT_CODE;
    const versionKey = editingDatasource?.runtime?.versionKey || this.defaultVersionKey();

    this.formRef.current?.setFieldsValue({
      PythonCode: code,
      RuntimeVersion: versionKey
    });
    this.seedName(name);
  };

  /** Opens on `name`, and takes the baseline once the state holds it. */
  seedName = (name: string): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: name });
    this.setState({ datasourceName: name, isNameOnEdit: !name }, (): void => {
      this.objectHash = this.getObjectHash();
    });
  };

  getObjectHash(): string {
    const form = this.formRef.current?.getFieldsValue();
    return JSON.stringify({ name: this.state.datasourceName, code: form?.PythonCode, version: form?.RuntimeVersion });
  }

  handleStartRename = (): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: this.state.datasourceName });
    this.setState({ isNameOnEdit: true });
  };

  /** Commits the typed name once its rules — the async uniqueness check among them — pass. */
  handleConfirmRename = async (): Promise<string | undefined> => {
    const name: string | undefined = await validateRenameField(this.formRef.current, "DatasourceName");
    if (name === undefined) return undefined;
    this.setState({ datasourceName: name, isNameOnEdit: false });
    return name;
  };

  handleAbortRename = (): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: this.state.datasourceName });
    this.setState({ isNameOnEdit: false });
  };

  /** The name a save writes: the committed one, or — mid-rename — the typed one once it passes. */
  resolveNameForSave = async (): Promise<string | undefined> => {
    if (this.state.isNameOnEdit) return this.handleConfirmRename();
    if (!this.state.datasourceName) {
      KMainFunctions.displayWarningNotification("Please enter a Datasource Name");
      return undefined;
    }
    return this.state.datasourceName;
  };

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }
    this.props.setShowModal(true);
  }

  nameValidator = async (_rule: unknown, value: string) => {
    if (!value) return;

    const existingDsName = this.props.sd?.datasources?.find(
      (ds) => ds.name.toLowerCase() === value.toLowerCase() && ds.id !== this.props.editingDatasource?.id
    );
    if (existingDsName) throw new Error("Datasource name already exists.");
  };

  saveDatasource = async (): Promise<void> => {
    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    const name: string | undefined = await this.resolveNameForSave();
    if (!name) {
      this.saveStatus.reset();
      return;
    }
    const form = this.formRef.current!.getFieldsValue();
    const selectedVersion =
      this.state.runtimeVersions.find((v) => v.versionKey === form.RuntimeVersion) ?? this.state.runtimeVersions[0];

    const ds = {
      name,
      datasourceType: EDatasourceType.PythonAction,
      code: form.PythonCode,
      runtime: selectedVersion
        ? { versionKey: selectedVersion.versionKey, displayVersion: selectedVersion.displayVersion }
        : undefined
    } as IDatasourceDto;

    if (this.props.editingDatasource?.id) {
      ds.id = this.props.editingDatasource.id;
    }

    await DatasourceService.SaveDatasource(ds)
      .then(async (response): Promise<void> => {
        if (response.status !== HttpStatusCode.Ok) {
          // A resolved response that is not 200: the trailing reset used to rescue this branch.
          this.saveStatus.fail();
          return;
        }
        this.props.sd.refreshActions();
        await this.props.sd.refreshDatabases();
        // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
        // A close confirmation this save answered ("Update & close") steps aside at once, so
        // the ✓ on the button is what is seen before the editor leaves.
        this.props.setShowModal(false);
        this.saveStatus.succeed((): void => {
          // The list behind the editor lights the row up; the button has already said "Saved".
          requestDatasourceReveal({ name: ds.name });
          this.closeDataSource();
        });
      })
      .catch((err): void => {
        this.saveStatus.fail();
        KMainFunctions.displayWarningNotification(err.response?.data?.message ?? err.message);
      });
  };

  handleEditorBeforeMount = (monacoInstance: typeof monacoEditor) => {
    injectMonacoStyleFixes();
    // Monaco cannot read CSS variables, so the theme is declared here and re-declared
    // whenever the app's appearance flips.
    defineCodeEditorTheme(monacoInstance);
    this.codeThemeObserver = watchCodeEditorTheme(monacoInstance);
  };

  handleEditorMount = (editor: monacoEditor.editor.IStandaloneCodeEditor) => {
    this.editorRef = editor;
    this.aiPendingEdit.attach(monacoEditor, editor);
    this.controls.attach(monacoEditor, editor);
    const model = editor.getModel();
    if (model) {
      this.requestDiagnostics(model.getValue());
      model.onDidChangeContent(() => this.handleEditorChange(model.getValue()));
    }
    this.registerCompletionProvider();
    this.registerHoverProvider();
  };

  /** The editor's own content listener writes the form field and refreshes diagnostics. */
  handleApplyPendingAiEdit = () => {
    this.aiPendingEdit.commit();
  };

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.aiPendingEdit.dispose();
    this.controls.dispose();
    this.codeThemeObserver?.disconnect();
    this.handleEditorChange.cancel();
    this.diagnosticsAbortController?.abort();
    this.completionProvider?.dispose();
    this.hoverProvider?.dispose();
  }

  registerHoverProvider = (): void => {
    const monaco = (window as unknown as { monaco: typeof monacoEditor }).monaco;
    if (!monaco || this.hoverProvider) return;

    this.hoverProvider = monaco.languages.registerHoverProvider("python", {
      provideHover: async (
        model: monacoEditor.editor.ITextModel,
        position: monacoEditor.Position
      ): Promise<monacoEditor.languages.Hover | null> => {
        const info = await LanguageServerService.GetHoverInfo(
          model.getValue(),
          model.getOffsetAt(position),
          this.props.editingDatasource?.id ?? null,
          "python"
        );
        if (!info) return null;

        return {
          range: {
            startLineNumber: info.startLineNumber + 1,
            startColumn: info.startColumn + 1,
            endLineNumber: info.endLineNumber + 1,
            endColumn: info.endColumn + 1
          },
          contents: [{ value: info.content, isTrusted: true }]
        };
      }
    });
  };

  registerCompletionProvider = (): void => {
    const monaco = (window as unknown as { monaco: typeof monacoEditor }).monaco;
    if (!monaco || this.completionProvider) return;

    this.completionProvider = monaco.languages.registerCompletionItemProvider("python", {
      triggerCharacters: ["."],
      provideCompletionItems: async (
        model: monacoEditor.editor.ITextModel,
        position: monacoEditor.Position
      ): Promise<monacoEditor.languages.CompletionList> => {
        const word = model.getWordUntilPosition(position);
        const range: monacoEditor.IRange = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };

        const items = await LanguageServerService.GetCompletions(
          model.getValue(),
          model.getOffsetAt(position),
          this.props.editingDatasource?.id ?? null,
          "python"
        );

        return {
          incomplete: true,
          suggestions: items.map((item) => ({
            label: item.label,
            kind: mapCompletionKind(monaco, item.kind),
            insertText: item.insertText,
            detail: item.detail,
            range,
            filterText: item.label,
            sortText: item.label
          }))
        };
      }
    });
  };

  handleEditorChange = debounce((value: string): void => {
    this.requestDiagnostics(value);
  }, 500);

  requestDiagnostics = async (value: string): Promise<void> => {
    const editor = this.editorRef;
    if (!editor) return;
    const model = editor.getModel();
    if (!model) return;
    const monaco = (window as unknown as { monaco: typeof monacoEditor }).monaco;
    if (!monaco) return;

    this.diagnosticsAbortController?.abort();
    this.diagnosticsAbortController = new AbortController();

    try {
      const diagnostics = await LanguageServerService.GetDiagnostics(
        value,
        this.props.editingDatasource?.id ?? null,
        this.diagnosticsAbortController.signal,
        "python"
      );
      const isAborted: boolean = this.diagnosticsAbortController?.signal.aborted ?? false;
      if (isAborted) return;

      const markers: monacoEditor.editor.IMarkerData[] = diagnostics.map((d) => ({
        severity:
          d.severity === 3
            ? monaco.MarkerSeverity.Error
            : d.severity === 2
              ? monaco.MarkerSeverity.Warning
              : monaco.MarkerSeverity.Info,
        startLineNumber: d.startLineNumber + 1,
        startColumn: d.startColumn + 1,
        endLineNumber: d.endLineNumber + 1,
        endColumn: d.endColumn + 1,
        message: d.message,
        source: "Kuika Language Services"
      }));

      monaco.editor.setModelMarkers(model, "python", markers);
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== "AbortError") {
        console.error("Python LSP diagnostics error:", error);
      }
    }
  };

  handleParseMethods = async (): Promise<ITestRunnerMethod[]> => {
    const code = this.formRef.current?.getFieldValue("PythonCode") || "";
    const response = await DatasourceService.ParsePython(code);
    return response.data.methods || [];
  };

  handleExecute = async (methodName: string, args: unknown[], _serverSide: boolean): Promise<string> => {
    const code = this.formRef.current?.getFieldValue("PythonCode") || "";
    const packages = this.state.installedLibraries.map((library) => ({ name: library.name, version: library.version }));
    const response = await DatasourceService.ExecutePython({
      code,
      methodName,
      args,
      projectId: this.props.sd.project?.id,
      packages
    });
    const data = response.data;
    if (data.ok) {
      return JSON.stringify(data.result, null, 2) ?? "undefined";
    }
    return `${data.errorType ?? "Error"}: ${data.errorMessage ?? "unknown error"}`;
  };

  handleFormatCode = async (): Promise<void> => {
    const editor = this.editorRef;
    if (!editor) return;
    const model = editor.getModel();
    if (!model) return;

    try {
      const formatted: string | null = await LanguageServerService.Format(model.getValue(), "python");
      if (formatted == null) return;

      editor.executeEdits("kuika-format", [{ range: model.getFullModelRange(), text: formatted }]);
      this.formRef.current?.setFieldsValue({ PythonCode: formatted });
    } catch (e) {
      KMainFunctions.displayWarningNotification("Format failed: " + (e as Error).message);
      return;
    }

    this.controls.markFormatted();
  };

  fetchPypiPackages = async (): Promise<void> => {
    try {
      this.setState({ isLoadingPypiPackages: true });
      const { pypiSearchQuery, pypiPagination } = this.state;
      const response = await PackageManagementService.ListPyPIPackages(
        pypiSearchQuery,
        pypiPagination.skip,
        pypiPagination.take
      );

      if (response && response.status === HttpStatusCode.Ok) {
        const newPackages: IPypiPackageItem[] = response.data.data.map((pkg) => ({
          id: pkg.id,
          version: pkg.version,
          description: pkg.description
        }));

        this.setState({
          pypiPackages: pypiPagination.skip > 0 ? [...this.state.pypiPackages, ...newPackages] : newPackages,
          pypiPagination: { ...this.state.pypiPagination, total: response.data.totalHits }
        });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingPypiPackages: false });
    }
  };

  handleLoadMorePypiPackages = (): void => {
    const { pypiPagination } = this.state;
    this.setState(
      { pypiPagination: { ...pypiPagination, skip: pypiPagination.skip + pypiPagination.take } },
      this.fetchPypiPackages
    );
  };

  addLibrary = async (packageId: string, version: string): Promise<void> => {
    try {
      this.setState({ activePackageLoading: packageId });

      if (this.props.sd.project?.id) {
        const request: ICreateLibraryRequest = {
          name: packageId,
          version,
          packageType: PackageType.Python,
          isActive: true,
          kPackageRepository_Id: null
        };

        const response = await PackageManagementService.CreateLibrary(request);

        if (response && response.status === HttpStatusCode.Created) {
          this.fetchInstalledLibraries();
          KMainFunctions.displaySuccessNotification(`Library ${packageId} added`);
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ activePackageLoading: undefined });
    }
  };

  fetchInstalledLibraries = async (): Promise<void> => {
    try {
      this.setState({ isLoadingInstalledLibraries: true });

      if (this.props.sd.project?.id) {
        const response = await PackageManagementService.GetLibrariesByProject(
          this.props.sd.project.id,
          PackageType.Python
        );

        if (response && response.status === HttpStatusCode.Ok) {
          this.setState({
            installedLibraries: response.data.map((lib) => {
              const library: IInstalledLibrary = {
                id: lib.id || "",
                name: lib.name || "",
                version: lib.version || ""
              };
              if ("description" in lib && lib.description) {
                library.description = lib.description as string;
              }
              return library;
            })
          });
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingInstalledLibraries: false });
    }
  };

  deleteInstalledLibrary = async (libraryId: string, libraryName: string): Promise<void> => {
    try {
      this.setState({ isLoadingInstalledLibraries: true });

      const response = await PackageManagementService.DeleteLibrary(libraryId);

      if (response && response.status === HttpStatusCode.NoContent) {
        await this.fetchInstalledLibraries();
        KMainFunctions.displaySuccessNotification(`Library ${libraryName} removed`);
      } else {
        KMainFunctions.displayErrorNotification(`Failed to remove library ${libraryName}`);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingInstalledLibraries: false });
    }
  };

  toggleLibraryExpansion = async (packageId: string): Promise<void> => {
    const { expandedLibrary, libraryVersions } = this.state;

    if (expandedLibrary === packageId) {
      this.setState({ expandedLibrary: undefined });
      return;
    }

    this.setState({ expandedLibrary: packageId });

    if (libraryVersions[packageId] && libraryVersions[packageId].length > 0) {
      return;
    }

    await this.fetchPackageVersions(packageId);
  };

  fetchPackageVersions = async (packageId: string): Promise<void> => {
    try {
      this.setState({ isLoadingVersions: true });

      const response = await PackageManagementService.GetPyPIPackageDetails(packageId);

      if (response && response.status === HttpStatusCode.Ok && response.data.versions) {
        const versions: IPackageVersion[] = response.data.versions.map((v) => ({ version: v.version }));
        this.setState((prevState) => ({
          libraryVersions: { ...prevState.libraryVersions, [packageId]: versions }
        }));
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingVersions: false });
    }
  };

  // Confirmation happens inline in the popover; this performs the update directly.
  changeLibraryVersion = async (
    libraryId: string,
    libraryName: string,
    _currentVersion: string,
    newVersion: string
  ): Promise<void> => {
    try {
      this.setState({ isLoadingInstalledLibraries: true });

      const libraryToUpdate = this.state.installedLibraries.find((lib) => lib.id === libraryId);
      if (!libraryToUpdate) {
        throw new Error("Library not found");
      }

      const updateRequest: IUpdateLibraryRequest = {
        name: libraryToUpdate.name,
        version: newVersion,
        packageType: PackageType.Python,
        isActive: true,
        kPackageRepository_Id: null
      };

      const response = await PackageManagementService.UpdateLibrary(libraryId, updateRequest);

      if (response && response.status === HttpStatusCode.NoContent) {
        await this.fetchInstalledLibraries();
        KMainFunctions.displaySuccessNotification(`Library ${libraryName} updated to ${newVersion}`);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isLoadingInstalledLibraries: false });
    }
  };

  render() {
    // The dimming overlay and the close-confirmation dialog ask the same question the button's
    // status answers; they lift while the ✓ is still up, which is when the editor is leaving anyway.
    const isSaving: boolean = this.state.saveStatus === "loading";

    return (
      <Form
        name="dsModal"
        ref={this.formRef}
        onFinish={this.saveDatasource}
        layout="vertical"
        initialValues={{
          DatasourceName: this.props.editingDatasource?.name || "",
          PythonCode: this.props.editingDatasource?.code || DEFAULT_CODE,
          RuntimeVersion: this.props.editingDatasource?.runtime?.versionKey || this.defaultVersionKey()
        }}
        style={{ height: "100%" }}
      >
        <KuikaSpinnerSmall spinning={isSaving}>
          <div className="kuika__code-editor__page">
            <div className="kuika__code-editor__shell">
              <CodeEditorHeader
                title={this.state.datasourceName || "Python Action"}
                renderTitle={(): React.ReactNode => (
                  <CodeEditorRenameTitle
                    identity={
                      <CodeDatasourceSwitcherChip
                        sd={this.props.sd}
                        setNextCallbackFunction={this.props.setNextCallbackFunction}
                        types={[EDatasourceType.PythonAction]}
                        current={this.props.editingDatasource}
                        name={this.state.datasourceName}
                        entityLabel="Python Action"
                        datasourceType={EDatasourceType.PythonAction}
                        triggerTestId="pythonActionSwitcherTrigger"
                        labelTestId="pythonActionNameLabel"
                        onManageAll={() => this.handleCloseDatasource()}
                      />
                    }
                    isRenaming={this.state.isNameOnEdit}
                    canAbortRename={!!this.state.datasourceName}
                    entityLabel="Python action"
                    fieldName="DatasourceName"
                    rules={[
                      { required: true, message: "Please enter a Datasource Name" },
                      { validator: this.nameValidator }
                    ]}
                    placeholder="Datasource name"
                    testIds={codeEditorRenameTestIds("pythonActionName")}
                    onStartRename={this.handleStartRename}
                    onConfirmRename={(): void => void this.handleConfirmRename()}
                    onAbortRename={this.handleAbortRename}
                  />
                )}
                backLabel="Back to Datasources"
                saveLabel={this.props.editingDatasource ? "UPDATE" : "CREATE"}
                saveStatus={this.state.saveStatus}
                isSaveDisabled={false}
                saveTestId="pythonActionCreateUpdateButton"
                onClose={() => this.handleCloseDatasource()}
                closeTestId="closeCreateActionPanel"
              />

              <div className="kuika__code-editor__body">
                <div className="kuika__code-editor__main">
                  <CodeEditorToolbar
                    {...this.controls.toolbarBinding}
                    onFormat={this.handleFormatCode}
                    formatHint={
                      <div>
                        <p>How TIDY UP Formats Your Code:</p>
                        <ul style={{ paddingLeft: "16px", margin: "4px 0" }}>
                          <li>Black-compatible formatting via Ruff</li>
                          <li>4 spaces per indentation level</li>
                          <li>Normalizes quotes, spacing and blank lines</li>
                        </ul>
                        {this.props.editingDatasource && (
                          <p style={{ marginTop: "8px" }}>
                            Formatting changes become permanent after you click UPDATE.
                          </p>
                        )}
                      </div>
                    }
                    trailing={
                      <>
                        <ExternalLibrariesButton
                          libraries={this.state.installedLibraries}
                          packages={this.state.pypiPackages}
                          versions={
                            this.state.expandedLibrary
                              ? this.state.libraryVersions[this.state.expandedLibrary] || EMPTY_VERSIONS
                              : EMPTY_VERSIONS
                          }
                          hasMorePackages={
                            this.state.pypiPagination.skip + this.state.pypiPagination.take <
                            this.state.pypiPagination.total
                          }
                          onOpen={() => this.fetchInstalledLibraries()}
                          onSearch={(query, view) => {
                            if (view === "installed") this.fetchInstalledLibraries();
                            else {
                              this.setState(
                                { pypiSearchQuery: query, pypiPagination: { ...this.state.pypiPagination, skip: 0 } },
                                this.fetchPypiPackages
                              );
                            }
                          }}
                          onExpand={this.toggleLibraryExpansion}
                          onAdd={this.addLibrary}
                          onDelete={this.deleteInstalledLibrary}
                          onChangeVersion={this.changeLibraryVersion}
                          onLoadMore={this.handleLoadMorePypiPackages}
                        />
                        {/* The runtime the action is pinned to: an editor control, not a
                            document field, so it sits on the toolbar with the rest of them. */}
                        <span className="kuika__code-editor__toolbar__field">
                          <span className="kuika__code-editor__toolbar__field__label">Python</span>
                          <Form.Item name="RuntimeVersion" noStyle>
                            <KuikaSelectbox
                              size="sm"
                              style={{ minWidth: 104 }}
                              options={this.state.runtimeVersions.map((v) => ({
                                value: v.versionKey,
                                label: v.displayVersion
                              }))}
                            />
                          </Form.Item>
                        </span>
                      </>
                    }
                  />

                  {this.state.pendingAiEdit && (
                    <AiPendingEditBar
                      plan={this.state.pendingAiEdit}
                      subject="code"
                      onApply={this.handleApplyPendingAiEdit}
                      onDiscard={this.aiPendingEdit.discard}
                      onChangeMode={this.aiPendingEdit.setMode}
                    />
                  )}

                  <div className="kuika__code-editor__editor">
                    <Form.Item name="PythonCode" noStyle>
                      <Editor
                        language="python"
                        theme={CODE_EDITOR_THEME}
                        beforeMount={this.handleEditorBeforeMount}
                        onMount={this.handleEditorMount as unknown as Parameters<typeof Editor>[0]["onMount"]}
                        options={pythonEditorOptions as unknown as Parameters<typeof Editor>[0]["options"]}
                      />
                    </Form.Item>
                  </div>

                  <TestRunner
                    onParseMethods={this.handleParseMethods}
                    onExecute={this.handleExecute}
                    showServerClientSplit={false}
                    runButtonLabel="Run"
                    emptyMessage="No functions found. Write top-level functions and open this panel."
                  />
                </div>

                <CodeEditorSplitter
                  orientation="vertical"
                  size={this.state.sidePanelWidth}
                  onSizeChange={(sidePanelWidth) => this.setState({ sidePanelWidth })}
                  minSize={240}
                  maxSize={520}
                  isCollapsed={!this.state.isSidePanelOpened}
                  onToggleCollapsed={(isCollapsed) => this.setState({ isSidePanelOpened: !isCollapsed })}
                />

                {this.state.isSidePanelOpened && (
                  <CodeEditorSidePanel
                    width={this.state.sidePanelWidth}
                    activeTab={this.state.sideTab}
                    onTabChange={(sideTab) => this.setState({ sideTab })}
                    parameterCount={0}
                    coachContent={
                      <AICodeGenerator
                        type={EAICodeGeneratorType.Python}
                        datasourceId={this.props.editingDatasource?.id}
                        onApplyCode={this.aiPendingEdit.request}
                      />
                    }
                    // A function's arguments are typed into the test console, so there is no
                    // second pane to offer here.
                    parametersContent={null}
                  />
                )}
              </div>
            </div>
          </div>
        </KuikaSpinnerSmall>
        <CloseConfirmationModal
          itemName={this.state.datasourceName}
          closeType={"Python Connection"}
          triggerModal={this.props.showModal}
          isLoading={isSaving}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </Form>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createPythonAction = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreatePythonActionProps>(CreatePythonAction));
export { createPythonAction as CreatePythonAction };
